/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role -- ARIA combobox popups use listbox semantics while the input retains keyboard focus. */
/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { Fragment, VNode, h } from "preact";
import { useId, useRef, useState } from "preact/hooks";
import { useTranslationContext } from "../../context/translation.js";
import { resolveFieldHandler, UIFormProps } from "../FormProvider.js";
import { ChoiceS } from "./InputChoiceStacked.js";
import { LabelWithTooltipMaybeRequired } from "./InputLine.js";

export function InputSelectMultiple<ChoiceVal>(
  props: {
    choices: ChoiceS<ChoiceVal>[];
    unique?: boolean;
    max?: number;
  } & UIFormProps<ChoiceVal>,
): VNode {
  const {
    label,
    choices,
    placeholder,
    tooltip,
    help,
    required,
    hidden,
    unique,
    max,
  } = props;
  const { i18n } = useTranslationContext();
  const { value, onChange, error } = resolveFieldHandler(
    props.handler,
    props.name,
  );

  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dirty, setDirty] = useState<boolean>();
  const id = `select-many-${useId()}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const normalizedFilter = (filter ?? "").toLocaleLowerCase();
  const list = (value ?? []) as string[];
  const choiceMap = new Map(
    choices.map((choice) => [String(choice.value), choice]),
  );

  if (hidden) return <Fragment />;

  const groupAlreadySelected = (choice: ChoiceS<ChoiceVal>): boolean =>
    !!choice.exclusiveGroup &&
    list.some((selectedValue) => {
      const selected = choiceMap.get(String(selectedValue));
      return (
        selected !== undefined &&
        selected.exclusiveGroup === choice.exclusiveGroup &&
        String(selected.value) !== String(choice.value)
      );
    });

  const unavailableReason = (choice: ChoiceS<ChoiceVal>) => {
    if (choice.disabled || groupAlreadySelected(choice)) {
      return choice.disabledReason ?? i18n.str`This option is not available.`;
    }
    if (max !== undefined && list.length >= max) {
      return i18n.str`The maximum number of selections has been reached.`;
    }
    return undefined;
  };

  const filteredChoices =
    filter === undefined
      ? undefined
      : choices.filter((choice) => {
          const match = [choice.label, choice.description].some((text) =>
            text?.toLocaleLowerCase().includes(normalizedFilter),
          );
          if (!unique) return match;
          return match && !list.includes(String(choice.value));
        });
  const noItems =
    filter === undefined
      ? undefined
      : filteredChoices === undefined || !filteredChoices.length;

  const choose = (choice: ChoiceS<ChoiceVal>) => {
    if (unavailableReason(choice)) return;
    if (unique && list.includes(String(choice.value))) return;
    onChange([...list, choice.value] as any);
    setFilter(undefined);
    setDirty(true);
  };

  return (
    <div class="sm:col-span-6">
      <LabelWithTooltipMaybeRequired
        label={label}
        technicalName={props.technicalName}
        required={required}
        tooltip={tooltip}
        name={id}
      />

      {!props.disabled ? (
        <div class="relative mt-2">
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={filter ?? ""}
            autoComplete="off"
            onChange={(event) => {
              setFilter(event.currentTarget.value);
              setActiveIndex(0);
              setDirty(true);
            }}
            onKeyDown={(event) => {
              if (!filteredChoices?.length) return;
              switch (event.key) {
                case "ArrowDown":
                  event.preventDefault();
                  setActiveIndex((current) =>
                    Math.min(current + 1, filteredChoices.length - 1),
                  );
                  break;
                case "ArrowUp":
                  event.preventDefault();
                  setActiveIndex((current) => Math.max(current - 1, 0));
                  break;
                case "Enter":
                  event.preventDefault();
                  choose(filteredChoices[activeIndex]);
                  break;
                case "Escape":
                  event.preventDefault();
                  setFilter(undefined);
                  break;
              }
            }}
            onBlur={() => setFilter(undefined)}
            onFocus={() => setFilter("")}
            onClick={() => setFilter("")}
            placeholder={placeholder}
            class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-gray-950 dark:text-gray-100 dark:ring-gray-600 sm:text-sm sm:leading-6"
            role="combobox"
            aria-controls={`${id}-options`}
            aria-expanded={filter !== undefined}
            aria-autocomplete="list"
            aria-activedescendant={
              filteredChoices?.length
                ? `${id}-option-${activeIndex}`
                : undefined
            }
            aria-describedby={
              [
                help ? `${id}-description` : undefined,
                error ? `${id}-error` : undefined,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
          />
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setFilter(filter === undefined ? "" : undefined);
              setActiveIndex(0);
              setDirty(true);
              inputRef.current?.focus();
            }}
            class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
            aria-label={i18n.str`Toggle choices`}
          >
            <svg
              class="h-5 w-5 text-gray-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          {noItems ? (
            <ul
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 dark:bg-gray-950 dark:ring-gray-700 sm:text-sm"
              id={`${id}-options`}
              role="listbox"
            >
              <li
                class="px-3 py-2 text-gray-500 dark:text-gray-400"
                role="option"
                aria-disabled="true"
                aria-selected="false"
              >
                <i18n.Translate>No element found</i18n.Translate>
              </li>
            </ul>
          ) : filteredChoices ? (
            <ul
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 dark:bg-gray-950 dark:ring-gray-700 sm:text-sm"
              id={`${id}-options`}
              role="listbox"
            >
              {filteredChoices.map((choice, index) => {
                const reason = unavailableReason(choice);
                const active = index === activeIndex;
                return (
                  <li key={String(choice.value)} role="none">
                    <button
                      type="button"
                      id={`${id}-option-${index}`}
                      role="option"
                      aria-selected="false"
                      aria-disabled={!!reason}
                      disabled={!!reason}
                      tabIndex={-1}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => choose(choice)}
                      class={`group w-full select-none px-3 py-2 text-left ${
                        reason
                          ? "cursor-not-allowed bg-gray-50 text-gray-500 dark:bg-gray-900 dark:text-gray-400"
                          : active
                            ? "cursor-pointer bg-indigo-600 text-white"
                            : "cursor-pointer text-gray-900 hover:bg-indigo-600 hover:text-white dark:text-gray-100"
                      }`}
                    >
                      <span class="block break-words font-medium">
                        {choice.label}
                      </span>
                      {choice.description ? (
                        <span
                          class={`mt-0.5 block break-words text-xs ${
                            !reason && active
                              ? "text-indigo-100"
                              : "text-gray-500 group-hover:text-indigo-100 dark:text-gray-400"
                          }`}
                        >
                          {choice.description}
                        </span>
                      ) : undefined}
                      {reason ? (
                        <span class="mt-1 block break-words text-xs font-medium text-amber-700 dark:text-amber-300">
                          {reason}
                        </span>
                      ) : undefined}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : undefined}
        </div>
      ) : undefined}

      <div class="flex flex-wrap gap-2">
        {list.map((selectedValue, index) => (
          <span
            key={`${selectedValue}-${index}`}
            class="mt-2 inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 p-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            {choiceMap.get(String(selectedValue))?.label ?? selectedValue}
            <button
              type="button"
              disabled={props.disabled}
              onClick={() => {
                const newValue = [...list];
                newValue.splice(index, 1);
                onChange(newValue as any);
                setFilter(undefined);
                setDirty(true);
              }}
              class="group relative h-5 w-5 rounded-sm hover:bg-gray-500/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed"
              aria-label={i18n.str`Remove ${choiceMap.get(String(selectedValue))?.label ?? selectedValue}`}
            >
              <svg
                viewBox="0 0 14 14"
                class="h-5 w-5 stroke-gray-700/50 group-hover:stroke-gray-700/75 dark:stroke-gray-200/60"
                aria-hidden="true"
              >
                <path d="M4 4l6 6m0-6l-6 6" />
              </svg>
            </button>
          </span>
        ))}
      </div>
      {help ? (
        <p
          class="mt-2 text-sm text-gray-500 dark:text-gray-400"
          id={`${id}-description`}
        >
          {help}
        </p>
      ) : undefined}
      {dirty !== undefined && error ? (
        <p class="mt-2 text-sm text-red-600" id={`${id}-error`}>
          {error}
        </p>
      ) : undefined}
    </div>
  );
}
